/**
 * Invite Block Component
 * 
 * Visually dominant section with referral link and benefit explanation.
 */

import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Copy, Check, Gift, Sparkles, Download, DollarSign, Users } from 'lucide-react'
import { Button } from '../ui/button'
import { Card, CardContent } from '../ui/card'
import { apiClient } from '../../lib/api/client'
import { toast } from '../../hooks/use-toast'

interface InviteBlockProps {
  referralCode?: string | null
  lifetimeStats?: {
    total_downloads_earned: number
    total_credits_earned: number
  }
}

export function InviteBlock({ referralCode, lifetimeStats }: InviteBlockProps) {
  const { t } = useTranslation('common')
  const [copied, setCopied] = useState(false)
  const [referralLink, setReferralLink] = useState<string>('')
  const [loading, setLoading] = useState(!referralCode)

  useEffect(() => {
    if (!referralCode) {
      // Generate referral code if not exists
      apiClient.referrals.getReferralLink(window.location.origin)
        .then(result => {
          setReferralLink(result.link)
          setLoading(false)
        })
        .catch(err => {
          console.error('Error getting referral link:', err)
          setLoading(false)
        })
    } else {
      setReferralLink(`${window.location.origin}?ref=${referralCode}`)
    }
  }, [referralCode])

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(referralLink)
      setCopied(true)
      toast({
        title: t('referrals.linkCopied', 'Link copied!'),
        description: t('referrals.shareWithFriends', 'Share it with your friends'),
      })
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  return (
    <Card className="invite-block overflow-hidden border-2 border-emerald-500/30 bg-gradient-to-br from-emerald-500/5 via-teal-500/5 to-cyan-500/5">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-24 -right-24 w-48 h-48 bg-emerald-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-12 -left-12 w-32 h-32 bg-teal-500/10 rounded-full blur-2xl" />
      </div>
      
      <CardContent className="invite-block-content relative p-6 md:p-8">
        {/* Header with icon */}
        <div className="invite-header flex items-center gap-3 mb-6">
          <div className="icon-wrapper p-3 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 text-white shadow-lg">
            <Gift className="h-6 w-6" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-foreground">
              {t('referrals.inviteFriends', 'Invite Friends')}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t('referrals.yourReferralLink', 'Your referral link')}
            </p>
          </div>
        </div>

        {/* Referral link input */}
        <div className="link-container flex flex-col md:flex-row gap-3 mb-6">
          <div className="link-input flex-1 flex items-center px-4 py-3 bg-background rounded-lg border border-border">
            <span className="text-sm md:text-base text-foreground truncate flex-1 font-mono">
              {loading ? t('common.loading', 'Loading...') : referralLink}
            </span>
          </div>
          <Button
            onClick={handleCopy}
            disabled={loading || !referralLink}
            className="copy-btn bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white px-6"
          >
            {copied ? (
              <>
                <Check className="h-4 w-4 mr-2" />
                {t('common.copied', 'Copied!')}
              </>
            ) : (
              <>
                <Copy className="h-4 w-4 mr-2" />
                {t('common.copyLink', 'Copy link')}
              </>
            )}
          </Button>
        </div>

        {/* Benefit explanation */}
        <div className="benefits-section bg-white/50 dark:bg-gray-900/50 rounded-xl p-4 md:p-6 space-y-4">
          <div className="benefit-header flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
            <Sparkles className="h-5 w-5" />
            <span className="font-semibold">
              {t('referrals.howItWorks', 'How it works')}
            </span>
          </div>
          
          <p className="text-sm text-muted-foreground">
            {t('referrals.benefitExplanation', 'When a friend signs up with your link and starts reading, you both receive rewards!')}
          </p>

          {/* Reward examples */}
          <div className="rewards-grid grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="reward-item flex items-center gap-3 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
              <div className="icon-circle w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <Gift className="h-4 w-4 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t('referrals.friendSignsUp', 'Friend signs up')}</p>
                <p className="text-sm font-semibold text-emerald-600">+3 {t('referrals.downloads', 'downloads')}</p>
              </div>
            </div>
            
            <div className="reward-item flex items-center gap-3 p-3 rounded-lg bg-teal-500/10 border border-teal-500/20">
              <div className="icon-circle w-8 h-8 rounded-full bg-teal-500/20 flex items-center justify-center">
                <Download className="h-4 w-4 text-teal-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t('referrals.friendDownloads', 'First download')}</p>
                <p className="text-sm font-semibold text-teal-600">+2 {t('referrals.downloads', 'downloads')}</p>
              </div>
            </div>
            
            <div className="reward-item flex items-center gap-3 p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/20">
              <div className="icon-circle w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center">
                <DollarSign className="h-4 w-4 text-cyan-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t('referrals.friendTranslates', 'First translation')}</p>
                <p className="text-sm font-semibold text-cyan-600">+$1 {t('referrals.credits', 'credits')}</p>
              </div>
            </div>

            {/* Milestone rewards for inviting multiple friends */}
            <div className="reward-item flex items-center gap-3 p-3 rounded-lg bg-violet-500/10 border border-violet-500/20">
              <div className="icon-circle w-8 h-8 rounded-full bg-violet-500/20 flex items-center justify-center">
                <Users className="h-4 w-4 text-violet-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t('referrals.invite3Friends', '3 active friends')}</p>
                <p className="text-sm font-semibold text-violet-600">+$5 {t('referrals.credits', 'credits')}</p>
              </div>
            </div>
            
            <div className="reward-item flex items-center gap-3 p-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
              <div className="icon-circle w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center">
                <Users className="h-4 w-4 text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t('referrals.invite5Friends', '5 active friends')}</p>
                <p className="text-sm font-semibold text-purple-600">+$10 {t('referrals.credits', 'credits')}</p>
              </div>
            </div>
            
            <div className="reward-item flex items-center gap-3 p-3 rounded-lg bg-fuchsia-500/10 border border-fuchsia-500/20">
              <div className="icon-circle w-8 h-8 rounded-full bg-fuchsia-500/20 flex items-center justify-center">
                <Users className="h-4 w-4 text-fuchsia-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t('referrals.invite10Friends', '10 active friends')}</p>
                <p className="text-sm font-semibold text-fuchsia-600">+$25 {t('referrals.credits', 'credits')}</p>
              </div>
            </div>
          </div>

          {/* Lifetime stats */}
          {lifetimeStats && (lifetimeStats.total_downloads_earned > 0 || lifetimeStats.total_credits_earned > 0) && (
            <div className="lifetime-stats pt-4 border-t border-border">
              <p className="text-xs text-muted-foreground">
                {t('referrals.lifetimeEarnings', 'So far you have earned')}:{' '}
                <span className="font-semibold text-foreground">
                  {lifetimeStats.total_downloads_earned} {t('referrals.downloads', 'downloads')}
                </span>
                {lifetimeStats.total_credits_earned > 0 && (
                  <>
                    {' '}{t('common.and', 'and')}{' '}
                    <span className="font-semibold text-foreground">
                      ${lifetimeStats.total_credits_earned.toFixed(2)} {t('referrals.inCredits', 'in credits')}
                    </span>
                  </>
                )}
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

